/**
 * Flagship Motion — shared utilities
 *
 * Exposed globally on window.FlagshipMotion so per-section scripts can use them
 * without needing a module bundler. Everything here is pure / side-effect free
 * except for the reduced-motion media query listener.
 */
(function () {
  'use strict';

  var FlagshipMotion = window.FlagshipMotion || {};

  // ---------------------------------------------------------------------------
  // Reduced motion flag (live — updates if the user toggles the OS setting)
  // ---------------------------------------------------------------------------
  var reduceQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  FlagshipMotion.reduceMotion = reduceQuery.matches;

  function onReduceChange(e) {
    FlagshipMotion.reduceMotion = e.matches;
    // Tell listeners (e.g. active sections) to rebuild their animations
    window.dispatchEvent(new CustomEvent('flagship:reduce-motion-change', {
      detail: { reduceMotion: e.matches }
    }));
  }

  if (typeof reduceQuery.addEventListener === 'function') {
    reduceQuery.addEventListener('change', onReduceChange);
  } else if (typeof reduceQuery.addListener === 'function') {
    // Safari < 14
    reduceQuery.addListener(onReduceChange);
  }

  // ---------------------------------------------------------------------------
  // Are we inside Shopify's Theme Editor?
  // ---------------------------------------------------------------------------
  FlagshipMotion.isEditor = !!(window.Shopify && window.Shopify.designMode);

  // ---------------------------------------------------------------------------
  // safeGsap — strips motion properties when reduced-motion is on.
  // Opacity/fade transitions are always kept; transforms are dropped.
  // Usage: gsap.to(el, FlagshipMotion.safeGsap({ x: 100, opacity: 1, duration: 1 }))
  // ---------------------------------------------------------------------------
  var MOTION_PROPS = [
    'x', 'y', 'z',
    'xPercent', 'yPercent',
    'rotation', 'rotationX', 'rotationY', 'rotationZ',
    'scale', 'scaleX', 'scaleY',
    'skewX', 'skewY'
  ];

  FlagshipMotion.safeGsap = function (vars) {
    if (!FlagshipMotion.reduceMotion) return vars;
    var safe = Object.assign({}, vars);
    MOTION_PROPS.forEach(function (key) { delete safe[key]; });
    return safe;
  };

  // ---------------------------------------------------------------------------
  // waitForFonts — resolves once document fonts are loaded (or immediately if
  // the Font Loading API isn't available). Call before creating ScrollTriggers
  // so line-heights don't shift mid-calculation.
  // ---------------------------------------------------------------------------
  FlagshipMotion.waitForFonts = function () {
    if (document.fonts && document.fonts.ready) {
      return document.fonts.ready;
    }
    return Promise.resolve();
  };

  // ---------------------------------------------------------------------------
  // refreshTriggers — call after any layout change. Debounced to once per frame.
  // ---------------------------------------------------------------------------
  var refreshScheduled = false;
  FlagshipMotion.refreshTriggers = function () {
    if (refreshScheduled) return;
    refreshScheduled = true;
    requestAnimationFrame(function () {
      refreshScheduled = false;
      if (window.ScrollTrigger) window.ScrollTrigger.refresh();
    });
  };

  // ---------------------------------------------------------------------------
  // onReady — small helper. Resolves when GSAP + ScrollTrigger are available
  // (they load deferred, so sections can't assume they're present synchronously).
  // ---------------------------------------------------------------------------
  FlagshipMotion.onReady = function (callback) {
    function attempt() {
      if (window.gsap && window.ScrollTrigger) {
        callback();
        return true;
      }
      return false;
    }
    if (attempt()) return;

    // Poll briefly until CDN scripts finish
    var start = Date.now();
    var interval = setInterval(function () {
      if (attempt() || Date.now() - start > 5000) {
        clearInterval(interval);
      }
    }, 50);
  };

  window.FlagshipMotion = FlagshipMotion;
})();
